import React, { useCallback, useRef, useState } from 'react'
import Select from 'react-select'
import useSWR from 'swr'
import { Badge, Banner, ChevronIcon, ImageSlot, TrashIcon } from '../../../components'
import { isPortraitCarouselWidgetType } from '../../../constants/widgetTypes'
import { useWidgetSite } from '../../../contexts/WidgetSiteContext'
import {
  PageTypeInfo, fetchMatchImages, fetchPageTypeConfig, uploadPinImage,
} from '../../../services/api'
import LinkRefSelector, {
  EMPTY_LINK_REF,
  linkRefToMetadata,
  metadataToLinkRef,
} from '../LinkRefSelector'
import formStyles from '../../../styles/forms.module.scss'
import selectStyles from '../../../styles/react-select.module.scss'
import type { CarouselMetadata, CarouselPin, PinType } from './CarouselEditor'
import {
  MATCH_LABEL_SWR_KEY, MATCH_SEARCH_HINT, MatchSelect, fetchMatchLabel,
} from '../../MatchSelect'
import { VodPinSelect, vodLabel } from './pinContentPickers'
import { fetchVodAsset } from '../../../services/vodApi'
import styles from './itemListEditor.module.scss'

// Pins are hand-placed cards the dynamic query flows around. Unlike hero
// slides, order is not array order: each pin names an explicit 0-based slot in
// the rendered rail, so this list is sorted by slot and its rows carry a slot
// number rather than a drag handle. Dragging happens in the preview strip
// below, where the slots are the thing on screen (CarouselPreviewPane); this
// list is where a slot is typed exactly and the rest of the pin is authored.
// Slots are shown 1-based — merchandisers count cards from one.

interface CarouselPinsEditorProps {
  metadata: CarouselMetadata
  onChange: (metadata: CarouselMetadata) => void
  /** Present once the widget row exists; pins are authored against that row,
   * so nothing can be pinned until it does. */
  widgetId?: string
  /** The carousel variant being authored — it decides the card crop. */
  widgetType: string
}

const PIN_TYPE_SHORT: Record<PinType, string> = { match: 'Match', vod: 'VOD', custom: 'Custom' }

const PIN_TYPE_OPTIONS: Array<{ value: PinType; label: string }> = [
  { value: 'match', label: 'A match' },
  { value: 'vod', label: 'A VOD asset' },
  { value: 'custom', label: 'A custom image + link' },
]

/** A custom pin linking a video or a channel: ACE renders it as that content's
 * card, and every field set on the pin overrides the content's own
 * (layout_pins.go: customPinCard). Mirrors LinkRef.contentKey there. */
const linksContent = (link: CarouselPin['link']): boolean =>
  !!link?.node_id && (link.page_type === 'video' || link.page_type === 'live')

/** Mirrors the schema's per-type required keys, so the row warns before the
 * API does. A custom pin's title is optional — artwork that carries its own
 * words needs no caption — but the card still has to be nameable: the title
 * and the alt text are the only text a custom pin has, and with neither, the
 * clients render a link with no accessible name at all. So one of the two is
 * demanded even though the schema, which cannot express "either", takes any.
 * A pin linking content needs neither: the content supplies both. */
const missingField = (pin: CarouselPin): string | null => {
  if (pin.type === 'match') return pin.match_id ? null : 'a match'
  if (pin.type === 'vod') return pin.asset_id ? null : 'a VOD asset'
  if (!pin.link) return 'a link'
  if (linksContent(pin.link)) return null
  if (!pin.image_url) return 'an image'
  if (!pin.title && !pin.alt_text) return 'a title or alt text'
  return null
}

const summaryFor = (pin: CarouselPin): string => {
  if (pin.type === 'custom') return pin.title || 'Custom card'
  if (pin.type === 'match') return 'No match picked'
  return 'No VOD asset picked'
}

/** tc_image_type.type_code for the generated match card — the artwork ACE puts
 * on a match's rail card (loadMatchCardImages in layout_dynamic.go). The
 * generator replaces this link in place, so a match has at most one. */
const MATCH_CARD_IMAGE_TYPE = 'match_card'

/** The row thumbnail is cropped like the rail it stands for, so the shape on
 * screen is the shape the card will be. */
const thumbClass = (base: string, portrait: boolean) =>
  portrait ? `${base} ${styles.thumbPortrait}` : base

/** The generated card for a pinned match, as the row thumbnail. Empty until the
 * generator's first successful render lands — the same point ACE gets a card to
 * serve. */
const MatchPinThumb: React.FC<{ matchId: string; portrait: boolean }> = ({ matchId, portrait }) => {
  const { data } = useSWR(['match-images', matchId], () => fetchMatchImages(matchId))
  const url = data?.images.find(img => img.type_code === MATCH_CARD_IMAGE_TYPE)?.cdn_url
  return url ? (
    <img
      src={url}
      alt=""
      className={thumbClass(styles.thumb, portrait)}
      title="Generated match card"
    />
  ) : (
    <span
      className={thumbClass(styles.thumbEmpty, portrait)}
      title="No match card generated yet"
    />
  )
}

const useVodPinAsset = (assetId: string | undefined) =>
  useSWR(assetId ? ['pin-vod-asset', assetId] : null, () => fetchVodAsset(assetId!), {
    revalidateOnFocus: false,
  }).data

/** A VOD pin shows the asset's own art, in the rail's orientation when it has one. */
const VodPinThumb: React.FC<{ assetId: string; portrait: boolean }> = ({ assetId, portrait }) => {
  const thumbs = useVodPinAsset(assetId)?.thumbnails ?? {}
  const url = (portrait ? thumbs['2x3'] ?? thumbs['9x16'] : undefined) ?? thumbs['16x9'] ?? Object.values(thumbs)[0]
  return url ? (
    <img src={url} alt="" className={thumbClass(styles.thumb, portrait)} />
  ) : (
    <span className={thumbClass(styles.thumbEmpty, portrait)} title="This asset has no thumbnail" />
  )
}

const PinThumb: React.FC<{ pin: CarouselPin; portrait: boolean }> = ({ pin, portrait }) => {
  if (pin.image_url) {
    return <img src={pin.image_url} alt="" className={thumbClass(styles.thumb, portrait)} />
  }
  if (pin.type === 'match' && pin.match_id) {
    return <MatchPinThumb matchId={pin.match_id} portrait={portrait} />
  }
  if (pin.type === 'vod' && pin.asset_id) {
    return <VodPinThumb assetId={pin.asset_id} portrait={portrait} />
  }
  return <span className={thumbClass(styles.thumbEmpty, portrait)} />
}

/** The pinned match named on the collapsed row, on the SWR key the expanded
 * picker resolves on — so opening a row costs no second request. */
const MatchPinLabel: React.FC<{ matchId: string; fallback: string }> = ({ matchId, fallback }) => {
  const { data: label } = useSWR([MATCH_LABEL_SWR_KEY, matchId], () => fetchMatchLabel(matchId), {
    revalidateOnFocus: false,
  })
  return (
    <span className={styles.summaryTitle} title={label}>
      {label ?? fallback}
    </span>
  )
}

const VodPinLabel: React.FC<{ assetId: string; fallback: string }> = ({ assetId, fallback }) => {
  const asset = useVodPinAsset(assetId)
  const label = asset ? vodLabel(asset) : undefined
  return (
    <span className={styles.summaryTitle} title={label}>
      {label ?? fallback}
    </span>
  )
}

const CarouselPinsEditor: React.FC<CarouselPinsEditorProps> = ({
  metadata, onChange, widgetId, widgetType,
}) => {
  const portrait = isPortraitCarouselWidgetType(widgetType)
  // siteCode gates the content pickers: they search only content ACE will
  // render on this site's rails, so a pin can't point at something the rail
  // refuses (which the preview would otherwise report as "not in the rail").
  const { siteId, siteCode } = useWidgetSite()
  const { data: typeConfig } = useSWR(
    siteId ? ['page-type-config', siteId] : null,
    () => fetchPageTypeConfig(siteId),
    { revalidateOnFocus: false },
  )
  const pageTypes: PageTypeInfo[] = typeConfig?.types || []

  const pins = metadata.pins ?? []

  // Latest metadata for async callbacks (image upload) that would otherwise
  // close over a stale render's pins and write to the wrong one.
  const metadataRef = useRef(metadata)
  metadataRef.current = metadata

  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const commitPins = useCallback(
    (next: CarouselPin[]) => {
      const cleaned = { ...metadataRef.current, pins: next.length ? next : undefined }
      if (!next.length) delete cleaned.pins
      onChange(cleaned)
    },
    [onChange],
  )

  const patchPinById = useCallback(
    (id: string, patch: Partial<CarouselPin>) => {
      const cur = metadataRef.current.pins ?? []
      commitPins(cur.map(p => (p.id === id ? { ...p, ...patch } : p)))
    },
    [commitPins],
  )

  const toggleExpanded = (id: string) =>
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const addPin = () => {
    const id = crypto.randomUUID()
    // Default to the first free slot so two new pins don't stack on slot 0.
    const taken = new Set(pins.map(p => p.position))
    let position = 0
    while (taken.has(position)) position += 1
    commitPins([...pins, { id, position, type: 'match' }])
    setExpanded(prev => new Set(prev).add(id))
  }

  const removePin = (id: string) => {
    commitPins((metadataRef.current.pins ?? []).filter(p => p.id !== id))
    setExpanded(prev => {
      if (!prev.has(id)) return prev
      const next = new Set(prev)
      next.delete(id)
      return next
    })
  }

  /** Switching type drops the fields that belonged to the old one, so the
   * saved config never carries a match_id on a custom pin. */
  const changeType = (pin: CarouselPin, type: PinType) =>
    patchPinById(pin.id, {
      type,
      match_id: undefined,
      asset_id: undefined,
      title: undefined,
      subtitle: undefined,
      image_url: undefined,
      alt_text: undefined,
      link: undefined,
    })

  const limit = metadata.limit ?? 25
  // Display order only — the stored array keeps author order, which is what
  // breaks ties between two pins on the same slot.
  const ordered = [...pins].sort((a, b) => a.position - b.position)

  return (
    <>
      <p className={formStyles.hint}>
        Pins hold a fixed slot in the rail; the query fills everything else around
        them. Drag a pinned card in the preview below to move it.
      </p>

      {!widgetId && (
        <Banner variant="info" message="Name the widget above to start pinning." />
      )}

      {ordered.map((pin) => {
        const isOpen = expanded.has(pin.id)
        const missing = missingField(pin)
        const outOfRange = pin.position >= limit
        return (
          <div key={pin.id} className={styles.slide}>
            <div className={styles.slideRow}>
              <PinThumb pin={pin} portrait={portrait} />

              <button type="button" className={styles.summary} onClick={() => toggleExpanded(pin.id)}>
                <span className={styles.summaryIndex}>
                  {`Slot ${pin.position + 1} · ${PIN_TYPE_SHORT[pin.type] ?? pin.type}`}
                </span>
                {pin.type === 'match' && pin.match_id ? (
                  <MatchPinLabel matchId={pin.match_id} fallback={pin.match_id} />
                ) : pin.type === 'vod' && pin.asset_id ? (
                  <VodPinLabel assetId={pin.asset_id} fallback={pin.asset_id} />
                ) : (
                  <span className={styles.summaryTitle}>{summaryFor(pin)}</span>
                )}
              </button>

              <div className={styles.rowActions}>
                {(missing || outOfRange) && (
                  <div className={styles.rowBadges}>
                    {missing && <Badge variant="warning" size="small">Needs {missing}</Badge>}
                    {outOfRange && (
                      <Badge variant="warning" size="small">Past the limit of {limit}</Badge>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  className={`${styles.rowIconBtn} ${styles.trashBtn}`}
                  onClick={() => removePin(pin.id)}
                  title="Remove pin"
                  aria-label="Remove pin"
                >
                  <TrashIcon />
                </button>
                <span className={styles.actionsDivider} aria-hidden="true" />
                <button
                  type="button"
                  className={`${styles.rowIconBtn} ${styles.chevronBtn} ${isOpen ? styles.chevronOpen : ''}`}
                  onClick={() => toggleExpanded(pin.id)}
                  title={isOpen ? 'Collapse' : 'Edit'}
                  aria-label={isOpen ? 'Collapse pin' : 'Edit pin'}
                  aria-expanded={isOpen}
                >
                  <ChevronIcon />
                </button>
              </div>
            </div>

            {isOpen && (
              <div className={styles.slideBody}>
                <div className={formStyles.fieldGroup}>
                  <label title="Which card position this pin occupies in the rail.">Slot</label>
                  <input
                    type="number"
                    min={1}
                    max={limit}
                    value={pin.position + 1}
                    onChange={(e) => {
                      const n = parseInt(e.target.value, 10)
                      patchPinById(pin.id, { position: Number.isFinite(n) && n > 0 ? n - 1 : 0 })
                    }}
                  />
                </div>

                <div className={formStyles.fieldGroup}>
                  <label>Pin type</label>
                  <Select
                    value={PIN_TYPE_OPTIONS.find(o => o.value === pin.type) ?? null}
                    onChange={(o) => o && changeType(pin, o.value)}
                    options={PIN_TYPE_OPTIONS}
                    className={selectStyles.reactSelect}
                    classNamePrefix="react-select"
                  />
                </div>

                {pin.type === 'match' && (
                  <div className={formStyles.fieldGroup}>
                    <label>Match</label>
                    <MatchSelect
                      value={pin.match_id ?? ''}
                      onChange={(id) => patchPinById(pin.id, { match_id: id || undefined })}
                      visibleOnSite={siteCode || undefined}
                    />
                    <p className={formStyles.hint}>{MATCH_SEARCH_HINT}</p>
                  </div>
                )}

                {pin.type === 'vod' && (
                  <div className={formStyles.fieldGroup}>
                    <label>VOD asset</label>
                    <VodPinSelect
                      value={pin.asset_id ?? ''}
                      onChange={(id) => patchPinById(pin.id, { asset_id: id || undefined })}
                      visibleOnSite={siteCode || undefined}
                    />
                  </div>
                )}

                {pin.type === 'custom' && (
                  <>
                    <div className={formStyles.fieldGroup}>
                      <label>Title <Badge variant="secondary" size="small">Public</Badge></label>
                      <input
                        type="text"
                        value={pin.title ?? ''}
                        onChange={(e) => patchPinById(pin.id, { title: e.target.value || undefined })}
                        placeholder={
                          linksContent(pin.link)
                            ? "Optional — blank uses the linked content's title"
                            : 'Optional — blank shows the artwork alone'
                        }
                      />
                    </div>

                    <div className={formStyles.fieldGroup}>
                      <label>Subtitle <Badge variant="secondary" size="small">Public</Badge></label>
                      <input
                        type="text"
                        value={pin.subtitle ?? ''}
                        onChange={(e) => patchPinById(pin.id, { subtitle: e.target.value || undefined })}
                        placeholder={
                          linksContent(pin.link) ? "Optional — blank uses the linked content's subtitle" : 'Optional'
                        }
                      />
                    </div>

                    {/* One image by design: carousel cards are fixed-aspect tiles
                        that scale, so there is no per-device art here the way a
                        full-bleed hero needs. The slot is capped to about a
                        card's size on screen rather than filling the form —
                        both crops land near 200px tall. */}
                    <div className={portrait ? styles.pinImagePortrait : styles.pinImage}>
                      <ImageSlot
                        label="Card image"
                        aspectRatio={portrait ? '3 / 4' : '16 / 9'}
                        // The portrait rail centre-crops its tiles while the
                        // landscape ones letterbox (tc-web CardTile.module.scss),
                        // so the slot previews whichever the rail will do —
                        // otherwise a 16:9 promo looks intact here and comes out
                        // cropped through the words.
                        fit={portrait ? 'cover' : 'contain'}
                        disabled={!widgetId}
                        image={pin.image_url ? { url: pin.image_url } : null}
                        onUpload={async (file) => {
                          const { cdn_url } = await uploadPinImage(widgetId!, file, pin.alt_text)
                          patchPinById(pin.id, { image_url: cdn_url })
                        }}
                        onRemove={
                          pin.image_url ? () => patchPinById(pin.id, { image_url: undefined }) : undefined
                        }
                      />
                    </div>
                    {linksContent(pin.link) && (
                      <p className={formStyles.hint}>Optional — without one, the card uses the linked content&apos;s artwork.</p>
                    )}

                    <div className={formStyles.fieldGroup}>
                      <label>Alt text <Badge variant="secondary" size="small">Public</Badge></label>
                      <input
                        type="text"
                        value={pin.alt_text ?? ''}
                        onChange={(e) => patchPinById(pin.id, { alt_text: e.target.value || undefined })}
                        placeholder="Describe the image for screen readers"
                      />
                    </div>

                    <LinkRefSelector
                      siteId={siteId}
                      allowExternal
                      value={pin.link ? metadataToLinkRef(pin.link) : EMPTY_LINK_REF}
                      onChange={(v) =>
                        patchPinById(pin.id, {
                          link: linkRefToMetadata(v, pageTypes) ?? undefined,
                        })
                      }
                    />
                  </>
                )}
              </div>
            )}
          </div>
        )
      })}

      {/* Nothing can be pinned before the widget row exists — naming the widget
          creates it (useWidgetFormState.handleNameBlur), and pin artwork
          uploads to /widgets/{id}/pin-images, so a custom pin authored first
          can never be finished. */}
      <button
        type="button"
        onClick={addPin}
        className={styles.addSlide}
        disabled={!widgetId}
        title={widgetId ? undefined : 'Name the widget above to start pinning'}
      >
        + Add pin
      </button>
    </>
  )
}

export default CarouselPinsEditor
